import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SubscriptionPlans from './SubscriptionPlans';
import { SubscriptionTier } from '../config/subscriptions';
import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';
import { StripeSessionStorage } from '../utils/secureStorage';

interface SignupWithSubscriptionProps {
  initialStep?: 'plans' | 'payment' | 'account' | 'verification' | 'complete';
}

const SignupWithSubscription: React.FC<SignupWithSubscriptionProps> = ({ initialStep = 'plans' }) => {
  const [step, setStep] = useState<'plans' | 'payment' | 'account' | 'verification' | 'complete'>(initialStep);
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier | null>(null);
  const [paymentData, setPaymentData] = useState({
    paymentIntentId: '',
    clientSecret: ''
  });
  const [accountData, setAccountData] = useState({
    email: '',
    confirmEmail: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [promotionCode, setPromotionCode] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);
  const { signup, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (isAuthenticated && user) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  // Load payment and tier info when starting from account step
  useEffect(() => {
    // Track TikTok page view for signup page
    if (typeof window !== 'undefined' && (window as any).ttq) {
      (window as any).ttq.track('ViewContent', {
        content_type: 'product',
        content_id: 'subscription_signup'
      });
    }
    
    if (initialStep === 'account') {
      // Use secure storage with automatic expiration
      const stripeSession = StripeSessionStorage.getStripeSession();
      const tierString = StripeSessionStorage.getSubscriptionTier();
      
      if (stripeSession && tierString) {
        // SECURITY: Re-verify the session to prevent manipulation
        verifySessionBeforeAccount(stripeSession, tierString);
      } else {
        // Missing or expired payment verification, redirect to signup
        StripeSessionStorage.clearStripeData(); // Clean up any partial data
        navigate('/signup');
      }
    }
  }, [initialStep, navigate]);

  // Step 1: Plan Selection → Payment
  const handlePlanSelect = (tier: SubscriptionTier) => {
    setSelectedTier(tier);
    setStep('payment');
  };

  // Step 2: Handle Stripe Checkout
  const handleStripeCheckout = async () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Selected tier:', selectedTier);
      console.log('Strip price ID:', selectedTier?.stripePriceId);
      console.log('Environment PREMIUM_PRICE_ID:', process.env.REACT_APP_STRIPE_PREMIUM_PRICE_ID);
    }
    
    const requestPayload = {
      priceId: selectedTier?.stripePriceId || '',
      successUrl: `${window.location.origin}/signup/account?session_id={CHECKOUT_SESSION_ID}&tier=${selectedTier?.id}`,
      cancelUrl: `${window.location.origin}/signup`,
      metadata: {
        tier: selectedTier?.id || 'unknown',
        promotion: 'elite_limited_time',
        signup_flow: 'true'
      }
    };
    if (process.env.NODE_ENV === 'development') {
      console.log('Request payload:', requestPayload);
    }
    
    if (!selectedTier?.stripePriceId) {
      setError(`Invalid subscription plan selected. Price ID: ${selectedTier?.stripePriceId}`);
      return;
    }

    setStripeLoading(true);
    setError(null);

    try {
      // Use new subscription-specific Cloudflare Workers endpoint
      const endpoint = 'https://eightythreelabs-subscription-checkout.yahya-darman-work.workers.dev/';
      
      // Create checkout session via backend API
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestPayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const { url } = await response.json();
      
      if (url) {
        // Open Stripe checkout in new tab
        window.open(url, '_blank', 'noopener,noreferrer');
        setStripeLoading(false);
        setError(null);
        
        // Show helpful message with account creation reminder
        const message = '✅ Stripe checkout opened in new tab! After payment, return here to create your account.';
        
        setTimeout(() => {
          alert(message);
        }, 500);
        
        // Set up listener for when user returns to continue account creation
        setPaymentData({ 
          paymentIntentId: 'pending_verification', 
          clientSecret: url 
        });
      } else {
        throw new Error('No checkout URL received');
      }
      
    } catch (error) {
      console.error('Checkout error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(`Failed to create checkout: ${errorMessage}`);
      setStripeLoading(false);
    }
  };

  // Step 3: Account Creation → Email Verification
  const handleAccountInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAccountData({
      ...accountData,
      [e.target.name]: e.target.value
    });
  };

  const handleNoPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    return false;
  };

  const handleAccountCreation = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!agreedToTerms) {
      setError('You must agree to the Terms of Service, Privacy Policy, and Cookie Policy to create an account');
      return;
    }

    if (accountData.email !== accountData.confirmEmail) {
      setError('Email addresses do not match');
      return;
    }

    if (accountData.password !== accountData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      console.log('Creating account with data:', {
        email: accountData.email,
        paymentIntentId: paymentData.paymentIntentId,
        subscriptionTier: selectedTier?.id
      });

      // Create account with payment information
      const result = await signup({
        email: accountData.email,
        password: accountData.password,
        paymentIntentId: paymentData.paymentIntentId,
        subscriptionTier: selectedTier?.id
      });

      console.log('Signup result:', result);

      if (result?.success) {
        console.log('Account creation successful! Redirecting to dashboard...');
        
        // Clear form data for security
        setAccountData({
          email: '',
          confirmEmail: '',
          password: '',
          confirmPassword: ''
        });
        
        // Track TikTok conversion for successful account creation
        if (typeof window !== 'undefined' && (window as any).ttq) {
          (window as any).ttq.track('CompleteRegistration', {
            content_type: 'product',
            content_id: selectedTier?.id || 'unknown'
          });
        }

        // Clear Stripe session data since account creation is complete
        StripeSessionStorage.clearStripeData();
        
        // For paid users, skip email verification and go directly to dashboard
        // Email verification can be handled in the background or required later
        console.log('🎉 Account created successfully! Redirecting to dashboard...');
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 1500);
        
      } else {
        console.error('Account creation failed:', result);
        setError(result?.error || 'Account creation failed. Please try again.');
      }
    } catch (err) {
      console.error('Account creation error:', err);
      setError('An unexpected error occurred during account creation. Please try again.');
    }
  };

  // Step 4: Email Verification → Dashboard
  const handleEmailVerified = () => {
    setStep('complete');
    // Redirect to dashboard after a brief success message
    setTimeout(() => {
      navigate('/');
    }, 2000);
  };

  // SECURITY: Re-verify Stripe session before account creation
  const verifySessionBeforeAccount = async (sessionId: string, tierString: string) => {
    try {
      const response = await fetch('/api/verify-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          expected_tier: tierString
        }),
      });

      if (!response.ok) {
        throw new Error(`Session verification failed: ${response.status}`);
      }

      const verificationResult = await response.json();
      
      if (verificationResult.valid && verificationResult.payment_status === 'paid') {
        // Session is valid, proceed with account creation
        setPaymentData({
          paymentIntentId: sessionId,
          clientSecret: 'verified'
        });
        
        // Load tier configuration
        import('../config/subscriptions').then(({ SUBSCRIPTION_TIERS }) => {
          const tier = SUBSCRIPTION_TIERS.find(t => t.id === verificationResult.tier);
          if (tier) {
            setSelectedTier(tier);
          } else {
            throw new Error('Invalid tier configuration');
          }
        });
      } else {
        throw new Error('Payment verification failed - session invalid or not paid');
      }
    } catch (error) {
      console.error('Session re-verification failed:', error);
      setError('Payment verification failed. Please try again.');
      // Clear potentially manipulated session data securely
      StripeSessionStorage.clearStripeData();
      // Redirect back to signup
      navigate('/signup', { replace: true });
    }
  };

  // Step 1: Plan Selection
  if (step === 'plans') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 dark:from-black dark:to-gray-900 flex flex-col">
        <PublicHeader />
        
        <div className="flex-1 py-4 sm:py-6 lg:py-8">
          <div className="max-w-6xl mx-auto px-3 sm:px-4 lg:px-6">
            <div className="text-center mb-6 sm:mb-8">
              <div className="inline-flex items-center bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 px-3 sm:px-4 py-2 rounded-full text-sm font-medium mb-4 sm:mb-6">
                🎯 Choose Your Plan
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
                Start Your Math Journey
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-gray-600 dark:text-gray-200 max-w-2xl mx-auto px-4 sm:px-0">
                Select a plan and complete payment to unlock interactive math games and challenges
              </p>
            </div>
            <SubscriptionPlans onSelectPlan={handlePlanSelect} />
          </div>
        </div>
        
        <PublicFooter />
      </div>
    );
  }

  // Step 2: Payment Processing
  if (step === 'payment') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 dark:from-black dark:to-gray-900 flex flex-col">
        <PublicHeader showAuthLinks={false} />
        
        <div className="flex-1 flex items-center justify-center py-6 sm:py-8 lg:py-12 px-3 sm:px-4 lg:px-6">
          <div className="max-w-md w-full space-y-4 sm:space-y-6 lg:space-y-8">
            <div className="text-center">
              <button
                onClick={() => setStep('plans')}
                className="mb-4 text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center text-sm sm:text-base"
              >
                ← Change Plan
              </button>
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-gray-900 dark:text-white">
                Complete Payment
              </h2>
              <p className="mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-300">
                {selectedTier?.name} - {selectedTier?.priceDisplay}/year
              </p>
            </div>

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-xl shadow-md">
              <div className="text-center">
                <div className="text-4xl mb-4">💳</div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Secure Payment with Stripe
                </h3>
                
                <button
                  onClick={handleStripeCheckout}
                  disabled={stripeLoading}
                  className="w-full flex justify-center py-3 px-4 border border-transparent text-base sm:text-lg font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {stripeLoading ? (
                    <div className="flex items-center">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Redirecting to Stripe...
                    </div>
                  ) : (
                    `Continue with ${selectedTier?.priceDisplay}/year`
                  )}
                </button>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Secure payment powered by Stripe
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <PublicFooter />
      </div>
    );
  }

  // Step 3: Account Creation
  if (step === 'account') {
    // Security check: ensure we have valid payment data before showing account form
    if (!paymentData.paymentIntentId || !selectedTier) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 dark:from-gray-900 dark:to-red-900 flex items-center justify-center p-4">
          <div className="max-w-md w-full text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">
              Payment Verification Required
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              We couldn't verify your payment information. Please complete the payment process first.
            </p>
            <Link
              to="/signup"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Start Signup Process
            </Link>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 dark:from-black dark:to-gray-900 flex flex-col">
        <PublicHeader showAuthLinks={false} />
        
        <div className="flex-1 py-6 sm:py-8 lg:py-12 px-3 sm:px-4 lg:px-6">
          <div className="max-w-2xl mx-auto">
            {/* Success Header */}
            <div className="text-center mb-6 sm:mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-green-100 dark:bg-green-900/50 rounded-full mb-4 sm:mb-6">
                <svg className="w-10 h-10 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
                Payment Confirmed!
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-gray-600 dark:text-gray-300 mb-2 px-4 sm:px-0">
                Welcome to {selectedTier?.name} ({selectedTier?.priceDisplay}/year)
              </p>
              <div className="inline-flex items-center space-x-2 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-4 py-2 rounded-full">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                </svg>
                <span>Secure payment processed by Stripe</span>
              </div>
            </div>

            {/* Account Creation Form */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 border border-gray-100 dark:border-gray-700">
              <div className="text-center mb-6 sm:mb-8">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Create Your Account
                </h2>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
                  Set up your account to start using your subscription
                </p>
              </div>

              <form onSubmit={handleAccountCreation} className="space-y-4 sm:space-y-5 lg:space-y-6">
                {error && (
                  <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 dark:border-red-600 p-4 rounded-r-lg">
                    <div className="flex">
                      <svg className="w-5 h-5 text-red-400 dark:text-red-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"></path>
                      </svg>
                      <div>
                        <p className="text-red-700 dark:text-red-300 font-medium">{error}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-4 sm:space-y-5">
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Email Address
                    </label>
                    <input
                      name="email"
                      type="email"
                      required
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-white bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm sm:text-base"
                      placeholder="your.email@example.com"
                      value={accountData.email}
                      onChange={handleAccountInputChange}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="confirmEmail" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Confirm Email Address
                    </label>
                    <input
                      name="confirmEmail"
                      type="email"
                      required
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-white bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm sm:text-base"
                      placeholder="Confirm your email address"
                      value={accountData.confirmEmail}
                      onChange={handleAccountInputChange}
                      onPaste={handleNoPaste}
                      onDrop={e => e.preventDefault()}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Copy/paste disabled for security</p>
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Password
                    </label>
                    <input
                      name="password"
                      type="password"
                      required
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-white bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm sm:text-base"
                      placeholder="Create a strong password"
                      value={accountData.password}
                      onChange={handleAccountInputChange}
                      onPaste={handleNoPaste}
                      onDrop={e => e.preventDefault()}
                    />
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Confirm Password
                    </label>
                    <input
                      name="confirmPassword"
                      type="password"
                      required
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-white bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm sm:text-base"
                      placeholder="Confirm your password"
                      value={accountData.confirmPassword}
                      onChange={handleAccountInputChange}
                      onPaste={handleNoPaste}
                      onDrop={e => e.preventDefault()}
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Copy/paste disabled for security</p>
                  </div>
                </div>

                {/* Legal Agreement Checkbox */}
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="agree-terms"
                        name="agree-terms"
                        type="checkbox"
                        checked={agreedToTerms}
                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                        required
                        className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 dark:bg-gray-800 rounded"
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="agree-terms" className="text-gray-700 dark:text-gray-300">
                        I agree to EightyThreeLabs'{' '}
                        <Link 
                          to="/legal-agreement" 
                          className="text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 font-medium underline"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Terms of Service, Privacy Policy, and Cookie Policy
                        </Link>
                        {' '}including binding arbitration and class action waiver provisions.
                      </label>
                    </div>
                  </div>
                  
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-600 p-3 rounded-r-lg">
                    <div className="flex">
                      <svg className="w-5 h-5 text-yellow-400 dark:text-yellow-500 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                      </svg>
                      <div className="text-sm">
                        <p className="text-yellow-700 dark:text-yellow-300 font-medium">Important Legal Notice</p>
                        <p className="text-yellow-600 dark:text-yellow-400 mt-1">
                          By checking this box, you agree to resolve disputes through binding arbitration and waive your right to participate in class-action lawsuits.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={!agreedToTerms}
                  className={`w-full flex items-center justify-center py-3 sm:py-4 px-4 sm:px-6 border border-transparent rounded-lg shadow-sm text-base sm:text-lg font-semibold text-white transition-all ${
                    agreedToTerms 
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500' 
                      : 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed'
                  }`}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                  </svg>
                  Complete Setup
                </button>

                {/* Security Notice */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 sm:p-4 border border-gray-200 dark:border-gray-600">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                    </svg>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        <strong>Secure Account Setup:</strong> Your payment has been processed securely. 
                        We use industry-standard encryption to protect your information.
                      </p>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        
        <PublicFooter />
      </div>
    );
  }

  // Step 4: Email Verification
  if (step === 'verification') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
        <PublicHeader showAuthLinks={false} />
        
        <div className="flex-1 flex items-center justify-center py-12 px-4">
          <div className="max-w-md w-full space-y-8 text-center">
            <div>
              <div className="text-4xl mb-4">📧</div>
              <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white">
                Verify Your Email
              </h2>
              <p className="mt-2 text-gray-600 dark:text-gray-300">
                We've sent a verification email to <strong>{accountData.email}</strong>
              </p>
              <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                Click the verification link in your email to complete your account setup.
              </p>
            </div>

            <div className="mt-8 space-y-4">
              <Link
                to="/login"
                className="w-full flex justify-center py-3 px-4 border border-transparent text-lg font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Go to Login Page
              </Link>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                After verifying your email, you can log in to access your account.
              </p>
              
              <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-700">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  <strong>Important:</strong> Check your spam/junk folder if you don't see the email within a few minutes.
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <PublicFooter />
      </div>
    );
  }

  // Step 5: Complete - Welcome
  if (step === 'complete') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
        <PublicHeader showAuthLinks={false} />
        
        <div className="flex-1 flex items-center justify-center py-12 px-4">
          <div className="max-w-md w-full space-y-8 text-center">
            <div>
              <div className="text-4xl mb-4">🎉</div>
              <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white">
                Welcome to EightyThreeLabs!
              </h2>
              <p className="mt-2 text-gray-600 dark:text-gray-300">
                Your account is ready. Redirecting to your dashboard...
              </p>
            </div>
            <div className="mt-4">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          </div>
        </div>
        
        <PublicFooter />
      </div>
    );
  }

  return null;
};

export default SignupWithSubscription;
export type { SignupWithSubscriptionProps };